import styled, { css } from 'styled-components';
import SVG from 'react-inlinesvg';
import { TTypography } from 'common/styles';
import MOText from './MOText';
import React from 'react';
import clx from 'classnames';

const StyledButton = styled.button<{
  buttonStyle: 'primary' | 'secondary' | 'alert'
  width?: number | string
  side?: 'left' | 'right'
  isFill?: boolean
  isStroke?: boolean
  isBack?: boolean,
  isHelpAndSupport?: boolean;
  isBorder?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 24px;
  &.small {
    padding: 12px 16px 12px 12px;
  }
  &.xsmall {
    padding: 8px 16px 8px 8px;
  }
  &.default.textless {
    padding: 16px;
  }
  svg {
    min-width: 24px;
    min-height: 24px;
    &.small {
      min-width: 10px;
      min-height: 10px;
    }
    &.xsmall {
      min-width: 16px;
      min-height: 16px;
    }
  }
  ${({ side }) => {
    if (side === 'left') {
      return css`
      &:not(.textless) {
        padding-left: 20px;
        & svg {
          margin-right: 10px;
        }
        &.small {
          padding-left: 17px;
          & svg {
            margin-right: 15px;
          }
        }
        &.xsmall {
          padding-left: 17px;
          & svg {
            margin-right: 15px;
          }
        }
      }
      `;
    }

    if (side === 'right') {
      return css`
      &:not(.textless) {
        padding-right: 20px;
        & svg {
          margin-left: 10px;
        }
        &.small {
          padding-right: 17px;
          & svg {
            margin-left: 10px;
          }
        }
      }
      `;
    }
  }}

  ${({ isBack }) => isBack
    ? css`
          & svg {
            transform: rotate(180deg);
          }
        `
    : ''}
${({
    theme: { buttons }, buttonStyle, isFill, isStroke, width, isHelpAndSupport, isBorder,
  }) => {
    const calculatedWidth = (() => {
      if (!width) return 'auto';
      if (typeof width === 'number') return `${width}px`;
      if (typeof width === 'string') return width;
    })();
    return css`
      cursor: ${buttons[buttonStyle]?.active.cursor};
      border: ${isBorder ? '1px solid var(--isio-secondary-secondary-20, #DCDDDD)' : buttons[buttonStyle]?.active.border}};
      color: ${buttons[buttonStyle]?.active.color};
      background: ${isHelpAndSupport ? '#353A8C' : buttons[buttonStyle]?.active.backgroundColor};
      width: ${calculatedWidth};
      & svg path {
        stroke: ${isStroke ? buttons[buttonStyle]?.active.color : 'inherit'};
        fill: ${isFill ? buttons[buttonStyle]?.active.color : 'inherit'};
      }
      & .text-span {
        color: ${buttons[buttonStyle]?.active.color};
        cursor: ${buttons[buttonStyle]?.active.cursor};
      }
      &:hover {
        background: ${isHelpAndSupport ? '#353A8C' : buttons[buttonStyle]?.active.hoverBackgroundColor};
        border: ${buttons[buttonStyle]?.active.hoverBorder};
        color: ${buttons[buttonStyle]?.active.hoverColor};
        & svg path {
          stroke: ${isStroke ? buttons[buttonStyle]?.active.hoverColor : 'inherit'};
          fill: ${isFill ? buttons[buttonStyle]?.active.hoverColor : 'inherit'};
        }
        & .text-span {
          color: ${buttons[buttonStyle]?.active.hoverColor};
        }
      }
      &:focus {
        background-color: ${buttons[buttonStyle]?.active.focusBackgroundColor};
        border: ${buttons[buttonStyle]?.active.focusBorder};
        color: ${buttons[buttonStyle]?.active.focusColor};
        outline-color: ${buttons[buttonStyle]?.active.focusOutline};
        & svg path {
          stroke: ${isStroke ? buttons[buttonStyle]?.active.focusColor : 'inherit'};
          fill: ${isFill ? buttons[buttonStyle]?.active.focusColor : 'inherit'};
        }
        & label {
          color: ${buttons[buttonStyle]?.active.focusColor};
        }
      }
      &:disabled {
        cursor: ${buttons[buttonStyle]?.disabled.cursor};
        background-color: ${buttons[buttonStyle]?.disabled.backgroundColor};
        border: ${buttons[buttonStyle]?.disabled.backgroundColor};
        color: ${buttons[buttonStyle]?.disabled.color};
        & .text-span {
          color: ${buttons[buttonStyle]?.disabled.color};
          cursor: ${buttons[buttonStyle]?.disabled.cursor};
        }
        & svg path {
          stroke: ${isStroke ? buttons[buttonStyle]?.disabled.color : 'inherit'};
          fill: ${isFill ? buttons[buttonStyle]?.disabled.color : 'inherit'};
        }
      }
    `;
  }}
`;

interface MOMainButtonInterface {
  text?: string
  id?: string
  width?: number | string
  typographyStyle?: TTypography
  buttonStyle?: 'primary' | 'secondary' | 'alert'
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  onClick?: (e?: any) => void
  variant?: 'default' | 'small' | 'xsmall'

  // svg icon handling stuff
  icon?: string
  iconSide?: 'left' | 'right'
  isFill?: boolean
  isStroke?: boolean
  isBack?: boolean
  ref?: any
  classNames?: string
  isHelpAndSupport?: boolean;
  isBorder?: boolean;
}

const MOMainButton: React.FC<MOMainButtonInterface> = React.forwardRef(({
  text = '',
  id,
  width,
  typographyStyle,
  buttonStyle = 'primary',
  variant = 'default',
  disabled = false,
  type = 'button',
  onClick = () => null,
  classNames = '',
  // svg icon handling stuff
  icon,
  iconSide,
  isFill = false,
  isStroke = false,
  isBack = false,
  isHelpAndSupport,
  isBorder,
}, ref: any) => {
  const side = icon && !iconSide ? 'left' : iconSide;
  const typography = (() => {
    if (typographyStyle) return typographyStyle;
    if (variant === 'default') return 'button';
    if (variant === 'small') return 'buttonSmall';
  })();

  return (
    <StyledButton
      className={clx(variant, classNames, { textless: !text })}
      ref={ref}
      {...{
        buttonStyle,
        width,
        type,
        id,
        onClick,
        disabled,
        side,
        isFill,
        isStroke,
        isBack,
        isHelpAndSupport,
        isBorder,
      }}
    >
      {icon && side === 'left' && <SVG src={icon} className={`${variant}`} />}
      <MOText
        typographyStyle={typography}
        className={`text-span ${variant}`}
      >
        {text}
      </MOText>
      {icon && side === 'right' && <SVG src={icon} className={`${variant}`} />}
    </StyledButton>
  );
});

export default MOMainButton;
